import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { profileActions, tierRepository, trackingService } from "../../../di/di";
import TrackingService from "../../../../data/service/impl/tracking_service_impl";

export const ProfileUIAction = Object.freeze({
  initial: "Initial",
  feedBackSendSuccess: "feedBackSendSuccess",
  feedBackSendError: "feedBackSendError",
  contactUsSendSuccess: "contactUsSendSuccess",
  contactUsSendError: "contactUsSendError",
  showErrorToast: "showErrorToast",
  showSuccessToast: "showSuccessToast",
  showWarningToast: "showWarningToast",
  showInfoToast: "showInfoToast",
  proceedToSubscribing: "proceedToSubscribing",
});

export const SubscribeStatus = Object.freeze({
  initial: "initial",
  ongoing: "ongoing",
  success: "success",
  failed: "failed",
});

const initialState = {
  tiers: [],
  user: {
    name: "",
    email: "",
    tierId: "",
    videoCallUsage: "",
    videoCallTimeRemaining: "",
    voiceCallTimeRemaining: "",
    planExpirationDate: "",
    messagingRemaining: "",
    tier: { name: "", description: {}, level: null },
  },
  tiersIsLoading: false,
  userCurrentTier: null,
  isToRefreshData: false,
  showLogoutModal: false,
  showContactModal: false,
  showFeedbackModal: false,
  showAddonsModal: false,
  showSuccessToast: false,
  successToastMessage: "",
  showErrorToast: false,
  errorToastMessage: "",
  warningToastMessage: "",
  showWarningToast: false,
  cancellingSubscription: false,
  isFailedToCancelSubs: false,
  isSuccessToCancelSubs: false,
  isFetchingAddons: false,
  addOns: [],
  isSubscribingToTier: false,
  isPurchasingAddon: false,
  isPurchasingAddonSuccess: false,
  isPurchaseShowToast: false,
  isContactUsSending: false,
  isFeedBackSending: false,
  cancelPlanModalOpen: false,
  reactivatePlanModalOpen: false,
  showMessageModal: false,
  showPricingModal: false,
  showConfetti: false,
  selectedTierId: null,
  isEditMode: false,
  gotoPaymentPage: false,
  paymentPageUrl: "",
  addonPurchaseStatus: {},
  addonStatusUpdates: [],
  isProcessingAddonPurchase: false,
  addOnPurchageURL: "",
  action: ProfileUIAction.initial,
  feedbackText: "",
  feedbackTextError: "",
  contactSubject: "",
  contactSubjectError: "",
  contactMessage: "",
  contactMessageError: "",
  subscribeStatus: SubscribeStatus.initial,
  promptMessage: "",
  isAddonsPurchasing: false,
  currentAddonPurchasingID: "",
  isPlanExpired: true,
  isReactivatingTier: false,
};

export const createProfileSlice = (userRepository, tierRepository, addonRepository, avatarRepository) => {
  return createSlice({
    name: "profile",
    initialState,
    reducers: {
      resetState: () => initialState,
      logout: (state) => {
        try {
          trackingService.trackEvent("logout_initiated", { userId: state.user.id, distinct_id: state.user.id, timestamp: new Date().toISOString() });
          state.user = initialState.user;
          userRepository.logout();
          trackingService.trackEvent("logout_success", { userId: state.user.id, distinct_id: state.user.id, timestamp: new Date().toISOString() });
        } catch (error) {
          trackingService.trackEvent("logout_failed", {
            userId: state.user.id,
            distinct_id: state.user.id,
            failure_reason: error.message,
            timestamp: new Date().toISOString(),
          });
        }
      },
      resetToRefreshDate: (state) => {
        state.isToRefreshData = false;
      },
      hidePurchaseToast: (state) => {
        state.isPurchaseShowToast = false;
      },
      showPurchaseToast: (state, action) => {
        state.isPurchaseShowToast = action.payload;
      },
      hideToasts: (state) => {
        state.showErrorToast = false;
        state.showSuccessToast = false;
      },
      setPurchaseSuccess: (state, action) => {
        console.log(action.payload);
        state.isPurchasingAddonSuccess = action.payload;
      },
      resetToastCancelSubs: (state) => {
        state.isSuccessToCancelSubs = false;
        state.isFailedToCancelSubs = false;
      },
      setShowLogoutModal: (state, action) => {
        state.showLogoutModal = action.payload;
      },
      setShowContactModal: (state, action) => {
        state.showContactModal = action.payload;
      },
      setShowFeedbackModal: (state, action) => {
        state.showFeedbackModal = action.payload;
      },
      setShowAddonsModal: (state, action) => {
        state.showAddonsModal = action.payload;
      },
      setShowMessageModal: (state, action) => {
        state.showMessageModal = action.payload;
      },
      setShowPricingModal: (state, action) => {
        state.showPricingModal = action.payload;
      },
      setShowConfetti: (state, action) => {
        state.showConfetti = action.payload;
      },
      setCancelPlanModalOpen: (state, action) => {
        state.cancelPlanModalOpen = action.payload;
      },
      setReactivatePlanModalOpen: (state, action) => {
        state.reactivatePlanModalOpen = action.payload;
      },
      setSelectedTierId: (state, action) => {
        state.selectedTierId = action.payload;
      },
      setIsToRefreshData: (state, action) => {
        state.isToRefreshData = action.payload;
      },
      setEditMode: (state, action) => {
        state.isEditMode = action.payload;
      },
      setAddonPurchaseStatus: (state, action) => {
        state.addonPurchaseStatus = {
          ...state.addonPurchaseStatus,
          [action.payload.addonId]: action.payload.status,
        };
      },
      addAddonStatusUpdate: (state, action) => {
        state.addonStatusUpdates.unshift(action.payload);
        if (state.addonStatusUpdates.length > 10) {
          state.addonStatusUpdates.pop();
        }
      },
      clearAddonStatusUpdates: (state) => {
        state.addonStatusUpdates = [];
      },
      resetAddonPurchaseURL: (state) => {
        state.addOnPurchageURL = "";
      },
      setAction: (state, action) => {
        state.action = action.payload;
      },
      setCurrentAddonPurchasingID: (state, action) => {
        state.currentAddonPurchasingID = action.payload;
      },
      setPromptMessage: (state, action) => {
        state.promptMessage = action.payload;
      },
      setFeedbackText: (state, action) => {
        state.feedbackText = action.payload;
      },
      setFeedbackTextError: (state, action) => {
        state.feedbackTextError = action.payload;
      },
      setContactSubject: (state, action) => {
        state.contactSubject = action.payload;
      },
      setContactSubjectError: (state, action) => {
        state.contactSubjectError = action.payload;
      },
      setContactMessage: (state, action) => {
        state.contactMessage = action.payload;
      },
      setContactMessageError: (state, action) => {
        state.contactMessageError = action.payload;
      },
      setSubscribeStatus: (state, action) => {
        state.subscribeStatus = action.payload;
      },
      onSubscribeSuccess: (state, action) => {
        state.subscribeStatus = SubscribeStatus.success;
        console.log("SUBSCRIPTION SUCCESS!");
      },
      onSubscribeFailed: (state, action) => {
        state.subscribeStatus = SubscribeStatus.failed;
        state.promptMessage = action.payload.message;
        state.action = ProfileUIAction.showErrorToast;
      },
      setIsAddonsPurchasing: (state, action) => {
        state.isAddonsPurchasing = action.payload;
      },
    },

    extraReducers: (builder) => {
      builder.addCase(getActiveTiers.fulfilled, (state, action) => {
        state.tiers = action.payload.data;
        state.tiersIsLoading = false;
      });

      builder
        .addCase(getUser.fulfilled, (state, action) => {
          const { payload } = action;
          state.user = payload;
          console.log("USER RESULT", state.user);
          state.tier = payload?.tier;

          // Check if the plan is expired
          const expiryDateString = state.user?.planExpirationDate;
          if (expiryDateString) {
            const expiryDate = new Date(expiryDateString);
            const currentDate = new Date();
            state.isPlanExpired = currentDate >= expiryDate;
          } else {
            state.isPlanExpired = true;
          }
        })
        .addCase(getUser.rejected, (state, action) => {});

      builder
        .addCase(cancelSubscription.pending, (state, action) => {
          trackingService.trackEvent("cancel_subscription_initiated", {
            userId: state.user.id,
            distinct_id: state.user.id,
            timestamp: new Date().toISOString(),
          });
          state.cancellingSubscription = true;
        })
        .addCase(cancelSubscription.fulfilled, (state, action) => {
          state.cancellingSubscription = false;
          state.isToRefreshData = true;
          const { success } = action.payload;
          if (success) {
            state.isSuccessToCancelSubs = true;
            state.successToastMessage = "Subscription cancelled successfully";
            trackingService.trackEvent("cancel_subscription_success", {
              userId: state.user.id,
              distinct_id: state.user.id,
              timestamp: new Date().toISOString(),
            });
          }

          state.cancelPlanModalOpen = false;
        })
        .addCase(cancelSubscription.rejected, (state, action) => {
          state.cancellingSubscription = false;
          state.isFailedToCancelSubs = true;
          state.errorToastMessage = "Failed to cancel subscription";
          trackingService.trackEvent("cancel_subscription_failed", {
            userId: state.user.id,
            distinct_id: state.user.id,
            failure_reason: action.payload.message,
            timestamp: new Date().toISOString(),
          });
        });

      builder
        .addCase(reactivateTier.pending, (state, action) => {
          state.isReactivatingTier = true;
          trackingService.trackEvent("reactivate_subscription_initiated", {
            userId: state.user.id,
            distinct_id: state.user.id,
            timestamp: new Date().toISOString(),
          });
        })
        .addCase(reactivateTier.fulfilled, (state, action) => {
          state.isReactivatingTier = false;
          state.isToRefreshData = true;
          state.reactivatePlanModalOpen = false;
          state.successToastMessage = "Subscription reactivated successfully";
          trackingService.trackEvent("reactivate_subscription_success", {
            userId: state.user.id,
            distinct_id: state.user.id,
            timestamp: new Date().toISOString(),
          });
        })
        .addCase(reactivateTier.rejected, (state, action) => {
          state.isReactivatingTier = false;
          state.errorToastMessage = "Failed to reactivate subscription";
          state.action = ProfileUIAction.showErrorToast;
          trackingService.trackEvent("reactivate_subscription_failed", {
            userId: state.user.id,
            distinct_id: state.user.id,
            failure_reason: action.payload.message,
            timestamp: new Date().toISOString(),
          });
        });

      builder
        .addCase(getActiveAddons.pending, (state, actions) => {
          state.isFetchingAddons = true;
        })
        .addCase(getActiveAddons.fulfilled, (state, actions) => {
          state.isFetchingAddons = false;
          state.addOns = actions.payload;
        })
        .addCase(getActiveAddons.rejected, (state, actions) => {
          state.isFetchingAddons = false;
        });

      builder
        .addCase(availAddons.pending, (state) => {
          state.isPurchasingAddon = true;
          state.isAddonsPurchasing = true;
          state.isProcessingAddonPurchase = true;
        })
        .addCase(availAddons.fulfilled, (state, action) => {
          state.isPurchasingAddon = false;
          state.isAddonsPurchasing = false;
          state.isToRefreshData = true;
          state.isProcessingAddonPurchase = false;
          state.addOnPurchageURL = action.payload.paymentPortal;
          addonRepository.connectSocket();
        })
        .addCase(availAddons.rejected, (state) => {
          state.isPurchasingAddon = false;
          state.isProcessingAddonPurchase = false;
        });

      builder
        .addCase(sendFeedBack.pending, (state) => {
          state.isFeedBackSending = true;
        })
        .addCase(sendFeedBack.fulfilled, (state, action) => {
          console.log("sendFeedBack.fulfilled", action.payload);
          state.isFeedBackSending = false;
          state.action = ProfileUIAction.feedBackSendSuccess;
          state.message = action.payload.message;
          state.showFeedbackModal = false;
          state.feedbackText = "";
          state.feedbackTextError = "";
        })
        .addCase(sendFeedBack.rejected, (state, action) => {
          console.log("sendFeedBack.rejected", action.payload);
          state.isFeedBackSending = false;
          if (action.payload.success === false) {
            if (action.payload.isFromFieldValidation === true) {
              state.feedbackTextError = action.payload.message;
            } else {
              state.action = ProfileUIAction.feedBackSendError;
              state.message = action.payload.message;
            }
          }        
        });

      builder
        .addCase(sendContactUs.pending, (state) => {
          state.isContactUsSending = true;
        })
        .addCase(sendContactUs.fulfilled, (state, action) => {
          state.isContactUsSending = false;
          state.action = ProfileUIAction.contactUsSendSuccess;
          state.message = action.payload.message;
          state.showContactModal = false;
          state.contactSubject = "";
          state.contactSubjectError = "";
          state.contactMessage = "";
          state.contactMessageError = "";
        })
        .addCase(sendContactUs.rejected, (state, action) => {
          state.isContactUsSending = false;
          if (action.payload.isFromFieldValidation === true) {
            state.contactSubjectError = action.payload.contactSubjectError;
            state.contactMessageError = action.payload.contactMessageError;
          } else {
            state.action = ProfileUIAction.contactUsSendError;
            state.message = action.payload.message;
          }
        });
    },
  });
};

export const getUser = createAsyncThunk("profile/getUser", async (credentials, { rejectWithValue, extra }) => {
  const userRepository = extra.userRepository;
  const result = await userRepository.getUser();
  return result;
});

export const getActiveTiers = createAsyncThunk("profile/getActiveTiers", async (credentials, { rejectWithValue, extra }) => {
  try {
    const tierAPIService = extra.tierRepository.tierApiService;
    const result = await tierAPIService.getActiveTiers();
    return result;
  } catch (err) {
    return err.response ? err.response.data : { success: false, message: "Something went wrong", data: [] };
  }
});

export const cancelSubscription = createAsyncThunk("profile/cancel-subscription", async (data, { rejectWithValue, extra }) => {
  try {
    return extra.tierRepository.tierApiService.unsubscribeTier(data);
  } catch (e) {
    return rejectWithValue(e.response?.data || { message: "Something went wrong" });
  }
});

export const reactivateTier = createAsyncThunk("profile/reactivateTier", async (data, { rejectWithValue, extra }) => {
  return extra.tierRepository.reactivateTier();
});

export const getActiveAddons = createAsyncThunk("profile/get-active-addons", async (data, { rejectWithValue, extra }) => {
  try {
    const { addonRepository } = extra;
    const response = await addonRepository.getActiveAddons();
    return response.data;
  } catch (e) {
    return rejectWithValue(e.response?.data || { message: "Failed to fetch active addons." });
  }
});

export const availAddons = createAsyncThunk("profile/avail-addons", async (data, { rejectWithValue, extra, getState }) => {
  try {
    const state = getState();
    console.log(state.profile, "isAddonsPurchasing");
    const { addonRepository } = extra;
    const response = await addonRepository.availAddon(data);
    addonRepository.checkAddonStatus(data.addonId);
    return response.data;
  } catch (e) {
    return rejectWithValue(e.response?.data || { message: "Failed to avail addon." });
  }
});

export const sendContactUs = createAsyncThunk("profile/sendContactUs", async (data, { rejectWithValue, extra, getState }) => {
  let userData = null;
  try {
    const { userRepository } = extra;
    const state = getState();
    userData = state.profile.user;
    const contactSubject = state.profile.contactSubject;
    const contactMessage = state.profile.contactMessage;

    trackingService.trackEvent("contact_us_initiated", { userId: userData?.id, distinct_id: userData?.id, timestamp: new Date().toISOString() });

    let hasError = false;
    let contactSubjectError = "";
    let contactMessageError = "";
    if (contactSubject.trim() === "") {
      contactSubjectError = "Field is required";
      trackingService.trackEvent("contact_us_failed", {
        userId: userData?.id,
        distinct_id: userData?.id,
        failure_reason: "Contact subject is required",
        timestamp: new Date().toISOString(),
      });
      hasError = true;
    }
    if (contactMessage.trim() === "") {
      contactMessageError = "Field is required";
      trackingService.trackEvent("contact_us_failed", {
        userId: userData?.id,
        distinct_id: userData?.id,
        failure_reason: "Contact message is required",
        timestamp: new Date().toISOString(),
      });
      hasError = true;
    }
    if (hasError) {
      trackingService.trackEvent("contact_us_failed", {
        userId: userData?.id,
        distinct_id: userData?.id,
        failure_reason: "Contact subject and message are required",
        timestamp: new Date().toISOString(),
      });
      return rejectWithValue({ success: false, contactSubjectError, contactMessageError, isFromFieldValidation: true });
    }
    const response = await userRepository.userSendContactUs({ subject: contactSubject, message: contactMessage });
    trackingService.trackEvent("contact_us_success", { timestamp: new Date().toISOString() });
    return response;
  } catch (e) {
    trackingService.trackEvent("contact_us_failed", { failure_reason: e.message, timestamp: new Date().toISOString() });
    return rejectWithValue({ success: false, message: "Failed sending message" });
  }
});

export const sendFeedBack = createAsyncThunk("profile/sendFeedBack", async (_, { rejectWithValue, extra, getState }) => {
  let userData = null;
  try {
    const { userRepository } = extra;
    const state = getState();
    const feedbackText = state.profile.feedbackText;
    userData = state.profile.user;

    trackingService.trackEvent("submit_feedback_initiated", { userId: userData?.id, distinct_id: userData?.id, timestamp: new Date().toISOString() });

    if (feedbackText.trim() === "") {
      trackingService.trackEvent("submit_feedback_failed", {
        userId: userData?.id,
        distinct_id: userData?.id,
        failure_reason: "Feedback text is required",
        timestamp: new Date().toISOString(),
      });
      console.log("sendFeedBack reject with value", { success: false, message: "Field is required", isFromFieldValidation: true });
      return rejectWithValue({ success: false, message: "Field is required", isFromFieldValidation: true });
    }

    const response = await userRepository.userSendFeedBack({ message: feedbackText });
    trackingService.trackEvent("submit_feedback_success", { userId: userData?.id, distinct_id: userData?.id, timestamp: new Date().toISOString() });
    console.log("sendFeedBack response", response);
    return response;
  } catch (e) {
    console.log("sendFeedBack catch", e);
    trackingService.trackEvent("submit_feedback_failed", {
      userId: userData?.id,
      distinct_id: userData?.id,
      failure_reason: e.message,
      timestamp: new Date().toISOString(),
    });
    return rejectWithValue({ success: false, message: "Failed to sending feedback" });
  }
});
